import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 16px;
`;

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  // max-width: 400px;
  max-width: 750px; // Increased max-width for desktop
  @media (max-width: 768px) {
    max-width: 100%; // Full width on mobile
    padding: 10px;
  }
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const SkeletonItem = styled(motion.div)`
  background: linear-gradient(90deg, #2c2c2c 25%, #3a3a3a 50%, #2c2c2c 75%);
  background-size: 200% 100%;
  border-radius: 8px;
  height: ${(props) => props.height || "20px"};
  width: ${(props) => props.width || "100%"};
  animation: shimmer 1.5s infinite linear;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonCircle = styled(SkeletonItem)`
  border-radius: 50%;
  height: ${(props) => props.size || "50px"};
  width: ${(props) => props.size || "50px"};
`;

const ShineEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
  animation: shine 1.5s infinite;

  @keyframes shine {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

interface LazyLoaderProps {
  type?: "box" | "card" | "list";
}

const LazyLoader: React.FC<LazyLoaderProps> = ({ type = "box" }) => {
  return (
    <LoaderContainer>
      <SkeletonWrapper>
        {type === "box" && (
          <>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonCircle size="60px" />
              <ShineEffect />
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonItem height="16px" width="80%" />
              <ShineEffect />
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonItem height="16px" width="60%" />
              <ShineEffect />
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonItem height="16px" width="90%" />
              <ShineEffect />
            </div>
          </>
        )}

        {type === "card" && (
          <>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonItem height="400px" width="100%" />
              <ShineEffect />
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonItem height="16px" width="70%" />
              <ShineEffect />
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonItem height="16px" width="50%" />
              <ShineEffect />
            </div>
          </>
        )}

        {type === "list" && (
          <>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonItem height="14px" width="80%" />
              <ShineEffect />
            </div>
            <div style={{ position: "relative", width: "100%" }}>
              <SkeletonItem height="14px" width="60%" />
              <ShineEffect />
            </div>
          </>
        )}
      </SkeletonWrapper>
    </LoaderContainer>
  );
};

export default LazyLoader;
